import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import GlobalHeader from '../components/GlobalHeader';
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import { useTenant } from '../contexts/TenantContext';
import { formatCurrency, formatPercent, formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { InfoTooltip } from '../components/InfoTooltip';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Download,
  Store,
  AlertCircle,
  TrendingUp,
  Package
} from 'lucide-react';

interface DistributionAvailabilityData {
  week: string;
  cards_total: {
    total_active_stores: number;
    avg_oos_pct: number | null;
    est_lost_dollars_total: number;
  };
  actions_suggestions: {
    increase_allocation: Array<{
      sku: string;
      name: string;
      est_lost_dollars: number;
      oos_pct: number;
      distribution_pct: number;
    }>;
    reduce_eoh_excess: Array<{
      sku: string;
      name: string;
      wos_store: number;
      velocity_5w: number;
    }>;
  };
  rows: Array<{
    sku: string;
    name: string;
    revenue: number;
    units: number;
    distribution_pct: number | null;
    oos_pct: number | null;
    est_lost_dollars: number | null;
    wos_store: number | null;
    wos_dc: number | null;
    velocity_5w: number;
    store_count?: number | null;
  }>;
  page: number;
  pageSize: number;
  totalRowCount: number;
}

const useDistributionAvailabilityData = (fiscalWeek: number, filters: any, sort: string, sortDir: string, page: number, retailerId: string | null) => {
  return useQuery<DistributionAvailabilityData>({
    queryKey: ['distribution-availability', fiscalWeek, filters, sort, sortDir, page, retailerId],
    queryFn: async () => {
      const params = new URLSearchParams({
        week: fiscalWeek.toString(),
        sort,
        dir: sortDir,
        page: page.toString(),
        pageSize: '50'
      });
      
      // Add retailer_id if selected
      if (retailerId) {
        params.append('retailer_id', retailerId);
      }
      
      if (Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }
      
      const response = await fetch(`/api/distribution-availability?${params}`);
      if (!response.ok) throw new Error('Failed to fetch distribution availability data');
      return response.json();
    },
    enabled: !!fiscalWeek
  });
};

export function DistributionAvailability() {
  const { selectedFiscalWeek } = useFiscalWeekContext();
  const { selectedRetailerId } = useTenant();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<any>({});
  const [sortField, setSortField] = useState('revenue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useDistributionAvailabilityData(
    selectedFiscalWeek, 
    { ...filters, q: searchQuery }, 
    sortField, 
    sortDirection, 
    currentPage,
    selectedRetailerId
  );

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const renderProgressBar = (value: number | null, colorClass: string = "bg-blue-500") => {
    if (value === null) return <span className="text-gray-500">—</span>;
    const percentage = Math.min(100, value * 100);
    return (
      <div className="flex items-center gap-2 min-w-[120px]">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${colorClass} transition-all duration-300 rounded-full`} 
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs text-gray-600 font-medium min-w-[45px] text-right">
          {formatPercent(value)}
        </span>
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader
          title="Distribution & Availability"
          subtitle="Error loading distribution availability data"
        />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center text-red-600">
            Error loading distribution availability data: {(error as Error).message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader
        title="Distribution & Availability"
        subtitle="Store-level SKU distribution and out-of-stock analysis"
        isLoading={isLoading}
      />
      
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">

        {isLoading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Header Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Store className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Total Active Stores</span>
                  </div>
                  <InfoTooltip content="Number of stores with sales activity this week. Used as denominator for distribution calculations." />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {formatNumber(data?.cards_total.total_active_stores || 0, 0)}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Avg Store OOS%</span>
                  </div>
                  <InfoTooltip content="Velocity-weighted average out-of-stock percentage across all SKUs. Higher velocity SKUs have more weight." />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {data?.cards_total.avg_oos_pct ? formatPercent(data.cards_total.avg_oos_pct) : '—'}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <TrendingUp className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Est Lost $ (Week)</span>
                  </div>
                  <InfoTooltip content="Total estimated lost sales across ALL SKUs with any OOS%. Calculated as Σ(Revenue × OOS%) for all products. Note: AI alerts below show only CRITICAL stockouts (OOS >10%), which is why the amounts differ." />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {formatCurrency(data?.cards_total.est_lost_dollars_total || 0, 0)}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  All SKUs with OOS% &gt; 0
                </div>
              </div>
            </div>

            {/* Actions Panel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200 px-6 py-3">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <h3 className="text-base font-bold text-gray-900">Increase Allocation</h3>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Critical stockouts: OOS &gt;10%, velocity &gt;10 units/week, expandable distribution
                  </p>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {data?.actions_suggestions.increase_allocation.length ? data.actions_suggestions.increase_allocation.slice(0, 5).map((sku) => (
                      <div key={sku.sku} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate text-sm">{sku.name}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            {formatPercent(sku.distribution_pct)} dist • {formatPercent(sku.oos_pct)} OOS • {formatNumber(sku.velocity_5w, 0)} vel
                          </div>
                        </div>
                        <div className="ml-3 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          {formatCurrency(sku.est_lost_dollars, 0)} lost
                        </div>
                      </div>
                    )) : (
                      <div className="text-sm text-gray-500 italic text-center py-6">
                        ✓ No critical stockouts detected (OOS &gt;10% with high velocity)
                      </div>
                    )}
                    <Button variant="outline" className="w-full mt-4 border-green-200 text-green-700 hover:bg-green-50" size="sm">
                      View All ({data?.actions_suggestions.increase_allocation.length || 0})
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-gray-200 px-6 py-3">
                  <div className="flex items-center space-x-2">
                    <Package className="h-5 w-5 text-orange-600" />
                    <h3 className="text-base font-bold text-gray-900">Reduce EOH Excess</h3>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Low velocity SKUs with high store WOS (&gt;10 weeks)
                  </p>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {data?.actions_suggestions.reduce_eoh_excess.slice(0, 5).map((sku) => (
                      <div key={sku.sku} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate text-sm">{sku.name}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            {formatNumber(sku.wos_store, 1)}w WOS • {formatNumber(sku.velocity_5w, 0)} vel
                          </div>
                        </div>
                        <div className="ml-3 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium border border-orange-200">
                          Excess
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" className="w-full mt-4 border-orange-200 text-orange-700 hover:bg-orange-50" size="sm">
                      View All ({data?.actions_suggestions.reduce_eoh_excess.length || 0})
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200 px-6 py-3">
                <h3 className="text-base font-bold text-gray-900">Filters & Search</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Search SKU/Name</label>
                    <Input
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Distribution</label>
                    <Select onValueChange={(value) => setFilters((prev: any) => ({ ...prev, distribution_bands: value === 'all' ? [] : [value] }))}>
                      <SelectTrigger className="border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                        <SelectValue placeholder="All distribution" />
                      </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Distribution</SelectItem>
                          <SelectItem value="<50">&lt; 50%</SelectItem>
                          <SelectItem value="50-80">50-80%</SelectItem>
                          <SelectItem value=">80">&gt; 80%</SelectItem>
                        </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">OOS Bands</label>
                    <Select onValueChange={(value) => setFilters((prev: any) => ({ ...prev, oos_bands: value === 'all' ? [] : [value] }))}>
                      <SelectTrigger className="border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                        <SelectValue placeholder="All OOS" />
                      </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All OOS%</SelectItem>
                          <SelectItem value="<5">&lt; 5%</SelectItem>
                          <SelectItem value="5-10">5-10%</SelectItem>
                          <SelectItem value=">10">&gt; 10%</SelectItem>
                        </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">WOS Bands</label>
                    <Select onValueChange={(value) => setFilters((prev: any) => ({ ...prev, wos_bands: value === 'all' ? [] : [value] }))}>
                      <SelectTrigger className="border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                        <SelectValue placeholder="All WOS" />
                      </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All WOS</SelectItem>
                          <SelectItem value="risk">Risk (&lt; 2w)</SelectItem>
                          <SelectItem value="healthy">Healthy (2-10w)</SelectItem>
                          <SelectItem value="excess">Excess (&gt; 10w)</SelectItem>
                        </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <Button variant="outline" className="w-full border-gray-300 hover:bg-gray-50">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Table */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900">SKU Distribution & Availability Analysis</h3>
                  <div className="text-xs text-gray-600">
                    Showing {data?.rows.length || 0} of {data?.totalRowCount || 0} SKUs
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-200">
                        <TableHead className="min-w-[200px] text-xs font-semibold text-gray-700 uppercase tracking-wide">SKU</TableHead>
                        <TableHead className="cursor-pointer text-xs font-semibold text-gray-700 uppercase tracking-wide hover:text-blue-600 transition-colors" onClick={() => handleSort('revenue')}>
                          <div className="flex items-center gap-2">
                            Revenue {getSortIcon('revenue')}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer text-xs font-semibold text-gray-700 uppercase tracking-wide hover:text-blue-600 transition-colors" onClick={() => handleSort('units')}>
                          <div className="flex items-center gap-2">
                            Units {getSortIcon('units')}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer text-xs font-semibold text-gray-700 uppercase tracking-wide hover:text-blue-600 transition-colors" onClick={() => handleSort('distribution_pct')}>
                          <div className="flex items-center gap-2">
                            Distribution % {getSortIcon('distribution_pct')}
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer text-xs font-semibold text-gray-700 uppercase tracking-wide hover:text-blue-600 transition-colors" onClick={() => handleSort('oos_pct')}>
                          <div className="flex items-center gap-2">
                            OOS % {getSortIcon('oos_pct')}
                          </div>
                        </TableHead>
                        <TableHead className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Est Lost $</TableHead>
                        <TableHead className="text-xs font-semibold text-gray-700 uppercase tracking-wide">WOS Store</TableHead>
                        <TableHead className="text-xs font-semibold text-gray-700 uppercase tracking-wide">WOS DC</TableHead>
                        <TableHead className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Velocity (5w)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.rows.map((row) => (
                        <TableRow key={row.sku} className="border-gray-200 hover:bg-gray-50 transition-colors">
                          <TableCell className="font-medium">
                            <div>
                              <div className="font-semibold text-gray-900 text-sm">{row.sku}</div>
                              <div className="text-xs text-gray-600 truncate max-w-[200px]">
                                {row.name}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-gray-900 font-medium">{formatCurrency(row.revenue, 0)}</TableCell>
                          <TableCell className="text-sm text-gray-900">{formatNumber(row.units, 0)}</TableCell>
                          <TableCell>
                            {renderProgressBar(row.distribution_pct, "bg-blue-500")}
                            <div className="text-[11px] text-gray-500 mt-1">Stores: {formatNumber(row.store_count ?? 0, 0)}</div>
                          </TableCell>
                          <TableCell>
                            {renderProgressBar(row.oos_pct, row.oos_pct && row.oos_pct > 0.1 ? "bg-red-500" : "bg-orange-500")}
                            <div className="text-[11px] text-gray-500 mt-1">OOS: {row.oos_pct !== null ? formatPercent(row.oos_pct) : '—'}</div>
                          </TableCell>
                          <TableCell className="text-sm text-gray-900">
                            {row.est_lost_dollars ? formatCurrency(row.est_lost_dollars, 0) : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-gray-900">
                            {row.wos_store ? formatNumber(row.wos_store, 1) + 'w' : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-gray-900">
                            {row.wos_dc ? formatNumber(row.wos_dc, 1) + 'w' : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-gray-900">
                            {formatNumber(row.velocity_5w, 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {data && data.totalRowCount > 50 && (
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      Page {currentPage} of {Math.ceil(data.totalRowCount / 50)}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="border-gray-300 hover:bg-gray-50"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage >= Math.ceil(data.totalRowCount / 50)}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="border-gray-300 hover:bg-gray-50"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
